import type {
  MatchResultSide,
  MatchResultState,
  MatchResultCardProps,
} from '@/components/match/MatchResultCard';

/** Where the match is, before the winnerless outcomes are folded in. */
export type MatchPhase = 'live' | 'upcoming' | 'finished';

/**
 * The card state for a row. A winnerless outcome (walkover, not played, dual withdrawal) wins
 * over the phase: such a match has no `match` row, so its phase reads `upcoming`.
 */
export function cardState(winnerless: boolean, phase: MatchPhase): MatchResultState {
  return winnerless ? 'winnerless' : phase;
}

export interface MatchCardWords {
  live: string;
  upcoming: string;
  finished: string;
  retiredShort: string;
}

/**
 * The status the card shows: the sr-only word and the visible header label. One ladder for
 * Matches and Watch. A retired finish is scored and has a winner, so it reads "Finished · ret.".
 */
export function matchCardStatus({
  state,
  outcomeWord,
  retired,
  words,
}: {
  state: MatchResultState;
  outcomeWord: string | null;
  retired: boolean;
  words: MatchCardWords;
}): { statusWord: string; status: MatchResultCardProps['header']['status'] } {
  if (state === 'live') {
    return { statusWord: words.live, status: { kind: 'live', label: words.live } };
  }
  if (state === 'upcoming') {
    return { statusWord: words.upcoming, status: { kind: 'upcoming', label: words.upcoming } };
  }
  if (state === 'winnerless') {
    return {
      statusWord: outcomeWord ?? words.finished,
      status: outcomeWord ? { kind: 'outcome', label: outcomeWord } : undefined,
    };
  }
  return retired
    ? {
        statusWord: `${words.finished} · ${words.retiredShort}`,
        status: { kind: 'retired', label: words.retiredShort },
      }
    : { statusWord: words.finished, status: undefined };
}

/**
 * The sr-only score line for one side ("Games won: 2. Game scores: 11, 7, 11"), only when the side has game scores.
 * The count is the games won, or the number of won games when that total is unknown.
 */
export function sideScoresLabel(
  format: (v: { count: number; scores: string }) => string,
  side: Pick<MatchResultSide, 'gamesWon' | 'games'>,
): string | undefined {
  if (side.games.length === 0) return undefined;
  const count = side.gamesWon ?? side.games.filter((g) => g.won).length;
  return format({ count, scores: side.games.map((g) => g.score).join(', ') });
}
